import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { emailService } from '../../services/emailService';

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file types (images only)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Please upload only image files (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file sizes (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setError('Each image must be less than 5MB');
      return;
    }
    
    // Limit to 3 attachments
    const totalFiles = [...attachments, ...files];
    if (totalFiles.length > 3) {
      setError('Maximum 3 images allowed');
      return;
    }
    
    setAttachments(totalFiles);
    setError('');
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setLoading(true);
    setUploadProgress(0);

    try {
      await emailService.sendSupportMessage(formData, attachments, setUploadProgress);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setAttachments([]);
      setUploadProgress(0);
      
      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900 relative overflow-hidden">
      {/* Animated Background Particles - Reduced on mobile */}
      <div className="fixed inset-0 pointer-events-none opacity-50 md:opacity-100">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-system-blue rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: Math.random() * 0.5 + 0.3,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue flex-shrink-0"></div>
            <span className="font-orbitron text-xs sm:text-sm tracking-wider text-system-blue uppercase">
              ［ Support Center ］
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black font-orbitron text-white mb-2 sm:mb-3 leading-tight">
            Contact <span className="text-system-blue">Support</span>
          </h1>
          <p className="text-gray-200 font-rajdhani text-sm sm:text-base leading-relaxed">
            Need help? Our team is here to assist you. Send us a message and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* Support Form */}
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 md:p-8"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Success Message */}
          {success && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-500/20 border-2 border-green-500/50 rounded text-green-400 font-rajdhani animate-slide-in">
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-xl sm:text-2xl flex-shrink-0">✓</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold mb-1 text-sm sm:text-base">Message Sent Successfully!</div>
                  <div className="text-xs sm:text-sm text-green-300">
                    We've received your message and will respond within 24-48 hours.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/20 border-2 border-red-500/50 rounded text-red-400 font-rajdhani">
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0">⚠️</span>
                <span className="text-xs sm:text-sm">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Name */}
            <div>
              <label className="block text-xs sm:text-sm font-rajdhani text-gray-200 uppercase tracking-wide mb-1.5 sm:mb-2">
                Your Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2.5 sm:px-4 sm:py-3 text-white font-rajdhani text-base focus:outline-none focus:border-system-blue transition-colors disabled:opacity-50"
                placeholder="Enter your full name"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs sm:text-sm font-rajdhani text-gray-200 uppercase tracking-wide mb-1.5 sm:mb-2">
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2.5 sm:px-4 sm:py-3 text-white font-rajdhani text-base focus:outline-none focus:border-system-blue transition-colors disabled:opacity-50"
                placeholder="your.email@example.com"
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs sm:text-sm font-rajdhani text-gray-200 uppercase tracking-wide mb-1.5 sm:mb-2">
                Subject <span className="text-red-400">*</span>
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                disabled={loading}
                className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2.5 sm:px-4 sm:py-3 text-white font-rajdhani text-base focus:outline-none focus:border-system-blue transition-colors disabled:opacity-50"
                required
              >
                <option value="">Select a subject...</option>
                <option value="General Inquiry">General Inquiry</option>
                <option value="Technical Issue">Technical Issue</option>
                <option value="Billing Question">Billing Question</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Bug Report">Bug Report</option>
                <option value="Account Help">Account Help</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs sm:text-sm font-rajdhani text-gray-200 uppercase tracking-wide mb-1.5 sm:mb-2">
                Message <span className="text-red-400">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                disabled={loading}
                rows={6}
                className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2.5 sm:px-4 sm:py-3 text-white font-rajdhani text-base focus:outline-none focus:border-system-blue transition-colors resize-none disabled:opacity-50"
                placeholder="Describe your issue or question in detail..."
                required
              />
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-300 font-rajdhani">
                {formData.message.length} / 1000 characters
              </div>
            </div>

            {/* Image Attachments */}
            <div>
              <label className="block text-xs sm:text-sm font-rajdhani text-gray-200 uppercase tracking-wide mb-1.5 sm:mb-2">
                Attachments (Optional)
              </label>
              <div className="space-y-2 sm:space-y-3">
                {/* File Input */}
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    disabled={loading || attachments.length >= 3}
                    className="hidden"
                    id="file-upload"
                  />
                  <div className="w-full px-3 py-2.5 sm:px-4 sm:py-3 bg-system-darker border-2 border-system-blue/30 rounded cursor-pointer hover:border-system-blue active:scale-[0.99] transition-all text-center disabled:opacity-50 touch-manipulation">
                    <span className="text-system-blue font-rajdhani font-bold text-sm sm:text-base">
                      {attachments.length >= 3 ? '✓ Maximum attachments reached' : '📎 Attach Screenshots (Max 3)'}
                    </span>
                    <p className="text-[10px] sm:text-xs text-gray-300 mt-1">PNG, JPG, GIF, WebP • Max 5MB each</p>
                  </div>
                </label>

                {/* Attached Files Preview */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-system-darker/50 border border-system-blue/20 rounded px-2.5 py-2 sm:px-3 sm:py-2"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-lg sm:text-xl flex-shrink-0">🖼️</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs sm:text-sm font-rajdhani text-white truncate">
                              {file.name}
                            </div>
                            <div className="text-[10px] sm:text-xs text-gray-300">
                              {(file.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          disabled={loading}
                          className="ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-400 hover:text-red-300 active:scale-95 transition-all disabled:opacity-50 touch-manipulation"
                          aria-label="Remove attachment"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex justify-between text-[10px] sm:text-xs font-rajdhani text-gray-200">
                  <span>Uploading attachments...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-system-darker rounded-full h-1.5 sm:h-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-system-blue to-system-purple transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full relative group px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-system-blue to-system-purple rounded font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 active:scale-95 hover:shadow-neon-blue overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 touch-manipulation"
            >
              <span className="relative z-10">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>SENDING...</span>
                  </span>
                ) : (
                  '📨 SEND MESSAGE'
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-system-purple to-system-blue opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </form>

          {/* Additional Info */}
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-system-blue/20">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                }}
              >
                <div className="text-system-blue text-xl sm:text-2xl mb-1.5 sm:mb-2">⏱️</div>
                <div className="font-orbitron text-xs sm:text-sm font-bold text-white mb-1">Response Time</div>
                <div className="font-rajdhani text-[10px] sm:text-xs text-gray-200">
                  We typically respond within 24-48 hours
                </div>
              </div>
              
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                }}
              >
                <div className="text-system-purple text-xl sm:text-2xl mb-1.5 sm:mb-2">📎</div>
                <div className="font-orbitron text-xs sm:text-sm font-bold text-white mb-1">Attachments</div>
                <div className="font-rajdhani text-[10px] sm:text-xs text-gray-200">
                  Upload screenshots to help us assist you better
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div 
          className="mt-6 sm:mt-8 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h2 className="font-orbitron text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3 sm:space-y-4 font-rajdhani">
            <div>
              <div className="text-system-blue font-bold mb-1 text-sm sm:text-base">How long does it take to get a response?</div>
              <div className="text-gray-200 text-xs sm:text-sm leading-relaxed">
                We aim to respond to all inquiries within 24-48 hours during business days.
              </div>
            </div>
            <div>
              <div className="text-system-blue font-bold mb-1 text-sm sm:text-base">What information should I include?</div>
              <div className="text-gray-200 text-xs sm:text-sm leading-relaxed">
                Please provide as much detail as possible about your issue, including screenshots if applicable.
              </div>
            </div>
            <div>
              <div className="text-system-blue font-bold mb-1 text-sm sm:text-base">Can I track my support request?</div>
              <div className="text-gray-200 text-xs sm:text-sm leading-relaxed">
                You'll receive a confirmation email with your request details. We'll follow up via email.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SupportPage;

